// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { useToast } from "@/components/ui/use-toast";
import { useAuthContext } from '@/contexts/AuthContext';
import { 
  ArrowLeft, 
  Calendar, 
  CheckCircle2, 
  CreditCard, 
  Download, 
  Edit, 
  FileText, 
  Loader2, 
  MoreHorizontal, 
  Pencil, 
  RefreshCw, 
  Trash2, 
  User, 
  Users,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ClipboardList,
  Receipt,
  CalendarIcon,
  AlertCircle,
  LayoutGrid,
  ChevronUp,
  ChevronDown,
  BarChart,
  Package,
  Building,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  TrendingUp,
  Activity,
  DollarSign,
  Star
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from '@/components/common/StatusBadge';
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { LoadingScreen } from '@/components/common';

interface SubscriptionDetail {
  id: string;
  display_id: string;
  subscription_type: 'main' | 'child';
  customer_name: string;
  legal_name: string;
  domain?: string;
  logo_url?: string;
  state: string;
  start_date: string;
  end_date: string | null;
  canceled_at: string | null;
  cancellation_reason?: string | null;
  currency_code: string | null;
  po_number: string | null;
  next_billing_at: string | null;
  payment_source_id: string | null;
  net_term_days: number | null;
  related_subscription: string | null;
  renewed_at: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
  hubspot_opportunity_id: string | null;
  created_by: string | null;
  due_date_type: string | null;
  due_date_day: number | null;
  due_date_next_month: boolean | null;
  parent_subscription_id: string | null;
  // Campos calculados/adicionais
  parent_subscription?: {
    id: string;
    display_id: string;
    subscription_type: string;
  };
  child_subscriptions?: {
    id: string;
    display_id: string;
    subscription_type: string;
    state: string;
    customer_name: string;
  }[];
  items: {
    id: string;
    subscription_plan: {
      id: string;
      name: string;
      billing_period_type: string;
      billing_period_duration: number;
      prices?: {
        currency: string;
        price_amount: number;
      }[];
    };
    quantity: number;
    unit_price: number;
    current_term_start: string | null;
    current_term_end: string | null;
    state: string | null;
    canceled_at: string | null;
    cancellation_reason: string | null;
    currency_code: string | null;
    next_billing_at: string | null;
    amount_price: number | null;
    billing_start_terms: string | null;
    billing_by: string | null;
    updated_at: string | null;
    updated_by: string | null;
    // Campos para itens consolidados
    is_consolidated?: boolean;
    total_amount?: number;
    source_subscriptions?: string[];
  }[];
  contacts: {
    id: string;
    contact_type: string;
    contact: {
      id: string;
      first_name: string;
      last_name: string | null;
      email: string;
      phone: string | null;
    }
  }[];
  orders?: {
    id: string;
    subscription_id: string;
    subscription_item_id?: string;
    subscription_plan_id?: string;
    operation_type: string;
    quantity_change: number | null;
    operation_date: string;
    created_at: string;
    notes?: string;
    subscription_item?: {
      subscription_plan: {
        name: string;
      }
    };
  }[];
}

const SubscriptionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  
  // Remover estados para o modal de edição de contatos
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [contactType, setContactType] = useState('');
  const [availableContacts, setAvailableContacts] = useState<Array<{id: string, name: string, email: string}>>([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  // Estados para o modal de geração de fatura
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState<Date>(new Date());
  const [dueDate, setDueDate] = useState<Date | undefined>(
    new Date(new Date().setDate(new Date().getDate() + 30))
  );
  const [invoiceAmount, setInvoiceAmount] = useState<number | ''>('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false);
  const [activeSubscriptionItems, setActiveSubscriptionItems] = useState<any[]>([]);

  // Estados para o modal de cancelamento de assinatura
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isSubmittingCancellation, setIsSubmittingCancellation] = useState(false);
  const [cancelReasonCategory, setCancelReasonCategory] = useState('');
  const [cancelDetails, setCancelDetails] = useState('');

  // Adicionar antes do return, após a declaração das outras variáveis de estado
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  
  // Estados para o modal de renovação de assinatura
  const [isRenewDialogOpen, setIsRenewDialogOpen] = useState(false);
  const [renewDate, setRenewDate] = useState<Date | undefined>(undefined);
  const [isSubmittingRenewal, setIsSubmittingRenewal] = useState(false);
  const [renewableItems, setRenewableItems] = useState<any[]>([]);
  const [renewItemsSelected, setRenewItemsSelected] = useState<Record<string, boolean>>({});
  const [calculatedEndDate, setCalculatedEndDate] = useState<Date | undefined>(undefined);
  
  // Adicionar após a declaração de outras funções de utilidade
  const getOpportunityStatusBadge = (status: string | undefined) => {
    if (!status) return <Badge variant="outline">Indefinido</Badge>;
    
    switch (status.toLowerCase()) {
      case 'open':
        return <Badge variant="secondary">Aberta</Badge>;
      case 'negotiation':
        return <Badge variant="default">Em Negociação</Badge>;
      case 'won':
        return <Badge variant="success" className="bg-green-100 text-green-800 hover:bg-green-200">Ganha</Badge>;
      case 'lost':
        return <Badge variant="destructive">Perdida</Badge>;
      case 'canceled':
        return <Badge variant="outline">Cancelada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Função para mapear o estágio do HubSpot para status interno
  const mapHubspotStageToStatus = (dealstage: string | undefined): string => {
    if (!dealstage) return 'open';
    
    if (dealstage.includes('closedwon')) return 'won';
    if (dealstage.includes('closedlost')) return 'lost';
    if (dealstage.includes('qualifiedtobuy') || 
        dealstage.includes('discovery') ||
        dealstage.includes('presentation') ||
        dealstage.includes('demo') ||
        dealstage.includes('decisionmaker') ||
        dealstage.includes('appointment')) return 'open';
    if (dealstage.includes('proposal') || dealstage.includes('negotiation')) return 'negotiation';
    
    return 'open';
  };
  
  // Add the missing getContactTypeLabel function
  const getContactTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'technical': 'Técnico',
      'administrative': 'Administrativo',
      'billing': 'Faturamento',
      'pedagogical': 'Pedagógico'
    };
    
    return types[type.toLowerCase()] || type;
  };

  // Helper functions for new subscription structure
  const getOwnerCustomer = () => {
    if (!subscription) return null;
    
    return {
      id: subscription.id, // Using subscription id as customer reference
      name: subscription.customer_name,
      legal_name: subscription.legal_name,
      logo_url: subscription.logo_url,
      domain: subscription.domain
    };
  };

  const getParticipantCustomers = () => {
    // Since the view doesn't include participant customers, we'll return empty array
    // This could be enhanced later if needed by querying subscription_customers table
    return [];
  };

  const getBillingContactCustomers = () => {
    // Since the view doesn't include billing contact customers, we'll return empty array
    // This could be enhanced later if needed by querying subscription_customers table
    return [];
  };

  const getCustomerRoleLabel = (role: string) => {
    const roles: Record<string, string> = {
      'owner': 'Proprietário',
      'participant': 'Participante',
      'billing_contact': 'Contato de Faturamento'
    };
    return roles[role] || role;
  };

  const getSubscriptionTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'main': 'Principal',
      'child': 'Filha'
    };
    return types[type] || type;
  };

  // Função para gerar iniciais do nome do cliente
  const getCustomerInitials = (name: string) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };
  
  // Modificar função para buscar oportunidades do HubSpot relacionadas
  const fetchRelatedOpportunities = async (subscriptionId: string) => {
    setOpportunitiesLoading(true);
    try {
      if (!subscriptionId) {
        throw new Error('ID da assinatura não fornecido');
      }

      // Primeiro, buscar a oportunidade diretamente relacionada à assinatura atual
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('hubspot_opportunity_id')
        .eq('id', subscriptionId)
        .single();
      
      if (subscriptionError) {
        console.error('Erro ao buscar ID da oportunidade relacionada:', subscriptionError);
        throw subscriptionError;
      }

      const hubspotOpportunityId = subscriptionData?.hubspot_opportunity_id;
      
      // Adicionar log para verificar o valor do ID
      console.log('Valor do hubspot_opportunity_id:', hubspotOpportunityId);
      
      // Se tiver um ID de oportunidade relacionado
      if (hubspotOpportunityId) {
        console.log(`Encontrado ID de oportunidade relacionada: ${hubspotOpportunityId}`);
        
        // Buscar detalhes da oportunidade
        const { data: opportunityData, error: opportunityError } = await supabase
          .from('hubspotopportunities')
          .select('*')
          .eq('id', hubspotOpportunityId);
        
        if (opportunityError) {
          console.error('Erro ao buscar detalhes da oportunidade:', opportunityError);
          throw opportunityError;
        }
        
        // Mapear os dados para o formato esperado
        const formattedOpportunities = opportunityData?.map(opp => ({
          ...opp,
          id: opp.id,
          name: opp.dealname,
          total_value: opp.amount || 0,
          currency_code: opp.deal_currency_code || 'BRL',
          status: mapHubspotStageToStatus(opp.dealstage),
          probability: opp.hs_deal_stage_probability || 0,
          expected_close_date: opp.closedate,
          created_at: opp.createdate
        })) || [];
        
        setOpportunities(formattedOpportunities);
      } else {
        // Se não tiver um ID relacionado, verificar se tem customer info
        if (subscription?.customer_name) {
          // Buscar oportunidades pelo nome da empresa no HubSpot
          const { data: customerOpportunities, error: customerOpportunitiesError } = await supabase
            .from('hubspotopportunities')
            .select('*')
            .ilike('company', `%${subscription.customer_name}%`)
            .order('createdate', { ascending: false });
          
          if (customerOpportunitiesError) {
            console.error('Erro ao buscar oportunidades por empresa:', customerOpportunitiesError);
            throw customerOpportunitiesError;
          }
          
          // Mapear os dados para o formato esperado
          const formattedOpportunities = customerOpportunities?.map(opp => ({
            ...opp,
            id: opp.id,
            name: opp.dealname,
            total_value: opp.amount || 0,
            currency_code: opp.deal_currency_code || 'BRL',
            status: mapHubspotStageToStatus(opp.dealstage),
            probability: opp.hs_deal_stage_probability || 0,
            expected_close_date: opp.closedate,
            created_at: opp.createdate
          })) || [];
          
          setOpportunities(formattedOpportunities);
        } else {
          // Nenhuma oportunidade relacionada encontrada
          setOpportunities([]);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar oportunidades relacionadas do HubSpot:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível carregar as oportunidades relacionadas' });
      setOpportunities([]);
    } finally {
      setOpportunitiesLoading(false);
    }
  };
  
  // Atualizar os useEffects para passar o ID da assinatura corretamente
  // Adicionar um useEffect para carregar um preview das oportunidades quando a assinatura é carregada
  useEffect(() => {
    if (id) {
      fetchRelatedOpportunities(id);
    }
  }, [id, subscription]);

  // Adicionar o useEffect para carregar oportunidades quando a aba muda
  useEffect(() => {
    if (activeTab === 'opportunities' && id) {
      fetchRelatedOpportunities(id);
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (id) {
      console.log('ID da assinatura:', id);
      fetchSubscriptionDetails(id);
    } else {
      console.error('ID da assinatura não fornecido');
      setLoading(false);
      toast({ variant: "destructive", title: "Erro", description: 'ID da assinatura não fornecido' });
    }
  }, [id]);

  const fetchSubscriptionDetails = async (subscriptionId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('subscriptions_')
        .select('*')
        .eq('id', subscriptionId)
        .single();

      if (error) {
        console.error('Erro ao buscar dados da assinatura:', error);
        throw error;
      }

      console.log('Dados da assinatura da view:', data);

      // If this is a child subscription, fetch parent subscription info
      let parentSubscription = null;
      if (data.parent_subscription_id) {
        const { data: parentData, error: parentError } = await supabase
          .from('subscriptions_')
          .select('id, display_id, subscription_type')
          .eq('id', data.parent_subscription_id)
          .single();
        
        if (!parentError && parentData) {
          parentSubscription = parentData;
        }
      }

      // If this is a main subscription, fetch child subscriptions
      let childSubscriptions = [];
      if (data.subscription_type === 'main') {
        const { data: childData, error: childError } = await supabase
          .from('subscriptions_')
          .select('id, display_id, subscription_type, state, customer_name')
          .eq('parent_subscription_id', subscriptionId);
        
        if (!childError && childData) {
          childSubscriptions = childData;
        }
      }

      // Buscar itens da assinatura (consolidado para main subscriptions)
      let itemsData = [];
      
      if (data.subscription_type === 'main' && childSubscriptions.length > 0) {
        // Para main subscriptions com filhas, buscar itens de todas as child subscriptions
        const childIds = childSubscriptions.map(child => child.id);
        
        const { data: childItemsData, error: childItemsError } = await supabase
          .from('subscription_items')
          .select(`
            id,
            subscription_id,
            quantity,
            unit_price,
            current_term_start,
            current_term_end,
            state,
            canceled_at,
            cancellation_reason,
            currency_code,
            next_billing_at,
            amount_price,
            billing_start_terms,
            billing_by,
            updated_at,
            updated_by,
            subscription_plan:subscription_plan_id (
              id,
              name,
              billing_period_type,
              billing_period_duration,
              prices:subscription_plan_prices (
                currency,
                price_amount
              )
            )
          `)
          .in('subscription_id', childIds);

        if (childItemsError) {
          console.error('Erro ao buscar itens das child subscriptions:', childItemsError);
          throw childItemsError;
        }

        // Consolidar itens por plano (somar quantidades e calcular preço médio)
        const consolidatedItems = new Map();
        
        childItemsData?.forEach(item => {
          const planId = item.subscription_plan?.id;
          if (!planId) return;
          
          if (consolidatedItems.has(planId)) {
            const existing = consolidatedItems.get(planId);
            existing.quantity += item.quantity;
            existing.total_amount += (item.quantity * item.unit_price);
            existing.source_subscriptions.push(item.subscription_id);
            // Calcular preço unitário médio ponderado
            existing.unit_price = existing.total_amount / existing.quantity;
          } else {
            consolidatedItems.set(planId, {
              id: `consolidated-${planId}`,
              subscription_plan: item.subscription_plan,
              quantity: item.quantity,
              unit_price: item.unit_price,
              total_amount: item.quantity * item.unit_price,
              currency_code: item.currency_code,
              state: 'active', // Consolidated items are considered active
              current_term_start: item.current_term_start,
              current_term_end: item.current_term_end,
              source_subscriptions: [item.subscription_id],
              is_consolidated: true
            });
          }
        });
        
        itemsData = Array.from(consolidatedItems.values());
        console.log('Itens consolidados das child subscriptions:', itemsData);
        
      } else {
        // Para child subscriptions ou main subscriptions sem filhas, buscar itens normalmente
        const { data: directItemsData, error: itemsError } = await supabase
        .from('subscription_items')
        .select(`
          id,
          quantity,
          unit_price,
          current_term_start,
          current_term_end,
          state,
          canceled_at,
          cancellation_reason,
          currency_code,
          next_billing_at,
          amount_price,
          billing_start_terms,
          billing_by,
          updated_at,
          updated_by,
          subscription_plan:subscription_plan_id (
            id,
            name,
            billing_period_type,
            billing_period_duration,
            prices:subscription_plan_prices (
              currency,
              price_amount
            )
          )
        `)
        .eq('subscription_id', subscriptionId);

      if (itemsError) {
        console.error('Erro ao buscar itens da assinatura:', itemsError);
        throw itemsError;
      }

        itemsData = directItemsData || [];
      console.log('Itens da assinatura:', itemsData);
      }
      
      // Verificar se os campos canceled_at e cancellation_reason estão presentes nos itens
      if (itemsData && itemsData.length > 0) {
        itemsData.forEach((item, index) => {
          console.log(`Item ${index} - canceled_at:`, item.canceled_at);
          console.log(`Item ${index} - cancellation_reason:`, item.cancellation_reason);
        });
      }

      // Buscar contatos da assinatura (apenas ativos)
      const { data: contactsData, error: contactsError } = await supabase
        .from('subscription_contacts')
        .select(`
          id,
          contact_type,
          contact:contact_id (
            id,
            first_name,
            last_name,
            email,
            phone
          )
        `)
        .eq('subscription_id', subscriptionId);

      if (contactsError) {
        console.error('Erro ao buscar contatos da assinatura:', contactsError);
        throw contactsError;
      }

      console.log('Contatos da assinatura:', contactsData);

      // Verificar se os dados da assinatura foram encontrados
      if (!data) {
        throw new Error('Assinatura não encontrada');
      }

      // Formatar dados para o componente
      const formattedItems = itemsData.map(item => {
        try {
          // Buscar o plano associado ao item
          const planData = item.subscription_plan;
          
          // Verificar se planData é um objeto ou um array
          const planObject = Array.isArray(planData) ? (planData[0] || {}) : planData;
          
          return {
            ...item,
            subscription_plan: {
              id: planObject.id || '',
              name: planObject.name || '',
              // @ts-ignore - Ignorar erros de tipagem do TypeScript
              billing_period_type: planObject.billing_period_type || '',
              // @ts-ignore - Ignorar erros de tipagem do TypeScript
              billing_period_duration: planObject.billing_period_duration || 1,
              // @ts-ignore - Ignorar erros de tipagem do TypeScript
              prices: planObject.prices || []
            }
          };
        } catch (error) {
          console.error('Erro ao formatar item:', error);
          return item;
        }
      });

      const formattedContacts = contactsData ? contactsData.map(contact => {
        // @ts-ignore - Ignorar erros de tipagem do TypeScript
        const contactData = contact.contact || {};
        return {
          id: contact.id,
          contact_type: contact.contact_type,
          contact: {
            // @ts-ignore - Ignorar erros de tipagem do TypeScript
            id: contactData.id || '',
            // @ts-ignore - Ignorar erros de tipagem do TypeScript
            first_name: contactData.first_name || '',
            // @ts-ignore - Ignorar erros de tipagem do TypeScript
            last_name: contactData.last_name,
            // @ts-ignore - Ignorar erros de tipagem do TypeScript
            email: contactData.email || '',
            // @ts-ignore - Ignorar erros de tipagem do TypeScript
            phone: contactData.phone
          }
        };
      }) : [];

      const formattedData: SubscriptionDetail = {
        ...data,
        parent_subscription: parentSubscription,
        child_subscriptions: childSubscriptions,
        // @ts-ignore - Ignorar erro de tipo
        items: formattedItems,
        contacts: formattedContacts
      };
      
      console.log('Dados formatados finais:', JSON.stringify(formattedData, null, 2));

      console.log('Dados formatados:', formattedData);
      setSubscription(formattedData);
    } catch (error) {
      console.error('Erro ao buscar detalhes da assinatura:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível carregar os detalhes da assinatura' });
      setLoading(false);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  // Update the getBillingPeriodLabel function to handle the new billing_period_duration field
  const getBillingPeriodLabel = (period: string, unit: string) => {
    const unitMap: Record<string, string> = {
      'day': 'dia(s)',
      'week': 'semana(s)',
      'month': 'mês(es)',
      'year': 'ano(s)'
    };
    
    const unitLabel = unitMap[unit.toLowerCase()] || unit;
    return `${period} ${unitLabel}`;
  };

  // Add a function to format billing period using duration
  const formatBillingPeriod = (periodType: string, duration: number) => {
    // Convert periodType to a unit
    let unit = 'month'; // default
    if (periodType === 'monthly') unit = 'month';
    else if (periodType === 'yearly' || periodType === 'annually') unit = 'year';
    else if (periodType === 'quarterly') unit = 'quarter';
    else if (periodType === 'semiannually') unit = 'half-year';
    
    return getBillingPeriodLabel(duration.toString(), unit);
  };

  // Add a function to translate billing_period_type
  const getBillingPeriodTypeLabel = (periodType: string) => {
    const periodTypeMap: Record<string, string> = {
      'monthly': 'Mensal',
      'yearly': 'Anual',
      'quarterly': 'Trimestral',
      'semiannually': 'Semestral',
      'one-time': 'Pagamento Único',
      'weekly': 'Semanal',
      'daily': 'Diário'
    };
    
    return periodTypeMap[periodType.toLowerCase()] || periodType;
  };

  const calculateTotalAmount = () => {
    if (!subscription) return 0;
    return subscription.items.reduce((total, item) => {
      // Para itens consolidados, usar total_amount se disponível
      if (item.is_consolidated && item.total_amount) {
        return total + item.total_amount;
      }
      return total + (item.quantity * item.unit_price);
    }, 0);
  };

  // Função para buscar contatos disponíveis
  const fetchAvailableContacts = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('id, first_name, last_name, email')
        .eq('customer_id', customerId);
      
      if (error) throw error;
      
      const formattedContacts = data.map(contact => ({
        id: contact.id,
        name: `${contact.first_name} ${contact.last_name || ''}`.trim(),
        email: contact.email
      }));
      
      setAvailableContacts(formattedContacts);
    } catch (error) {
      console.error('Erro ao buscar contatos disponíveis:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível carregar os contatos do cliente' });
    }
  };

  // Função para abrir o modal de adição de contato
  const openAddContactModal = () => {
    // For now, we'll disable this functionality since we don't have customer_id from the view
    // This could be enhanced later by querying the subscription_customers table
    toast({ variant: "destructive", title: "Erro", description: 'Funcionalidade de contatos temporariamente indisponível com a nova estrutura' });
  };

  // Função para adicionar um contato
  const addContact = async () => {
    if (!id || !selectedContactId || !contactType) {
      toast({ variant: "destructive", title: "Erro", description: 'Por favor, preencha todos os campos' });
      return;
    }
    
    setIsSubmittingContact(true);
    
    try {
      // Adicionar novo contato
      const { error } = await supabase
        .from('subscription_contacts')
        .insert({
          subscription_id: id,
          contact_id: selectedContactId,
          contact_type: contactType
        });
      
      if (error) throw error;
      
      toast({ title: "Sucesso", description: 'Contato adicionado com sucesso' });
      
      // Recarregar os dados da assinatura
      await fetchSubscriptionDetails(id);
      setIsAddingContact(false);
    } catch (error) {
      console.error('Erro ao adicionar contato:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível adicionar o contato' });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  // Função para remover um contato
  const removeContact = async (contactId: string) => {
    if (!id || !contactId) return;
    
    try {
      const { error } = await supabase
        .from('subscription_contacts')
        .delete()
        .eq('id', contactId);
      
      if (error) throw error;
      
      toast({ title: "Sucesso", description: 'Contato removido com sucesso' });
      
      // Recarregar os dados da assinatura
      await fetchSubscriptionDetails(id);
    } catch (error) {
      console.error('Erro ao remover contato:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível remover o contato' });
    }
  };

  // Add a function to fetch orders
  const fetchOrders = async (subscriptionId: string) => {
    setOrdersLoading(true);
    try {
      // Fetch orders for this subscription
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          subscription_id,
          subscription_item_id,
          subscription_plan_id,
          operation_type,
          quantity_change,
          operation_date,
          created_at,
          notes,
          subscription_items:subscription_item_id (
            id,
            subscription_plan:subscription_plan_id (
              id,
              name
            )
          ),
          subscription_plans:subscription_plan_id (
            id,
            name
          )
        `)
        .eq('subscription_id', subscriptionId)
        .order('operation_date', { ascending: false });
      
      if (ordersError) {
        console.error('Erro ao buscar histórico de operações:', ordersError);
        toast({ variant: "destructive", title: "Erro", description: 'Não foi possível carregar o histórico de operações' });
        return;
      }
      
      // Formatar os orders para o formato esperado
      const formattedOrders = orders?.map(order => {
        // Determinar o nome do plano
        let planName = 'Plano não especificado';
        
        // Usar type assertions para acessar as propriedades com segurança
        const orderAny = order as any;
        
        // Tentar obter o nome do plano do item da assinatura
        if (orderAny.subscription_items && 
            typeof orderAny.subscription_items === 'object' && 
            orderAny.subscription_items.subscription_plan && 
            typeof orderAny.subscription_items.subscription_plan === 'object' && 
            orderAny.subscription_items.subscription_plan.name) {
          planName = String(orderAny.subscription_items.subscription_plan.name);
        } 
        // Ou tentar obter diretamente do plano de assinatura
        else if (orderAny.subscription_plans && 
                typeof orderAny.subscription_plans === 'object' && 
                orderAny.subscription_plans.name) {
          planName = String(orderAny.subscription_plans.name);
        }
        
        return {
          id: order.id,
          subscription_id: order.subscription_id,
          subscription_item_id: orderAny.subscription_item_id as string,
          operation_type: order.operation_type,
          quantity_change: order.quantity_change,
          operation_date: order.operation_date,
          created_at: order.created_at,
          notes: orderAny.notes as string,
          subscription_item: {
            subscription_plan: {
              name: planName
            }
          }
        };
      }) || [];
      
      // Atualizar o estado da assinatura com os orders formatados
      setSubscription(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          orders: formattedOrders
        } as SubscriptionDetail;
      });
    } catch (error) {
      console.error('Erro ao buscar ordens:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível carregar as ordens' });
    } finally {
      setOrdersLoading(false);
    }
  };

  // Update useEffect to fetch orders when tab changes
  useEffect(() => {
    if (id && activeTab === 'orders' && subscription && !subscription.orders) {
      fetchOrders(id);
    }
  }, [id, activeTab, subscription]);

  // Add a function to translate operation types
  const getOperationTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'criação': 'Criação',
      'aumento': 'Aumento',
      'redução': 'Redução',
      'cancelamento': 'Cancelamento',
      'renovação': 'Renovação',
      'faturamento': 'Faturamento'
    };
    
    return types[type.toLowerCase()] || type;
  };

  // Adicionar função para gerar número de fatura no formato 366131-987
  const generateInvoiceNumber = () => {
    // Gerar números aleatórios para a primeira parte (6 dígitos)
    let firstPart = '';
    for (let i = 0; i < 6; i++) {
      firstPart += Math.floor(Math.random() * 10).toString();
    }
    
    // Gerar números aleatórios para a segunda parte (3 dígitos)
    let secondPart = '';
    for (let i = 0; i < 3; i++) {
      secondPart += Math.floor(Math.random() * 10).toString();
    }
    
    // Combinar as partes com o hífen
    return `${firstPart}-${secondPart}`;
  };

  // Adicionar função para processar a geração de fatura
  const handleGenerateInvoice = async () => {
    if (!subscription || !id) return;
    
    if (!invoiceNumber || !invoiceDate || !dueDate) {
      toast({ variant: "destructive", title: "Erro", description: 'Por favor, preencha todos os campos obrigatórios' });
      return;
    }
    
    setIsSubmittingInvoice(true);
    
    try {
      console.log('Iniciando geração de fatura...');
      
      // Buscar todos os itens ativos da assinatura
      const { data: activeItems, error: itemsError } = await supabase
        .from('subscription_items')
        .select(`
          id,
          quantity,
          unit_price,
          subscription_plan_id,
          subscription_plan:subscription_plan_id (
            id,
            name,
            prices:subscription_plan_prices (
              currency,
              price_amount
            )
          )
        `)
        .eq('subscription_id', id)
        .neq('state', 'canceled');
      
      if (itemsError) {
        console.error('Erro ao buscar itens da assinatura:', itemsError);
        throw itemsError;
      }
      
      if (!activeItems || activeItems.length === 0) {
        throw new Error('Não há itens ativos nesta assinatura');
      }
      
      console.log('Itens ativos encontrados:', activeItems);
      
      // Calcular o valor total de todos os itens ativos
      const totalAmount = activeItems.reduce((sum, item) => {
        return sum + (item.quantity * item.unit_price);
      }, 0);
      
      console.log('Valor total calculado:', totalAmount);
      
      // Criar a fatura principal
      console.log('Criando fatura principal...');
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          subscription_id: id, // Referência direta à assinatura
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate.toISOString(),
          amount_due: totalAmount,
        currency: subscription.currency_code || 'BRL',
        due_date: dueDate.toISOString(),
          status: 'pending',
          created_by: user?.id || null,
          updated_by: user?.id || null
        })
        .select()
        .single();
      
      if (invoiceError) {
        console.error('Erro ao criar fatura:', invoiceError);
        throw invoiceError;
      }
      
      console.log('Fatura criada com sucesso:', invoice);
      
      // Criar os itens da fatura
      console.log('Criando itens da fatura...');
      const invoiceItemsToInsert = activeItems.map(item => {
        const itemAmount = item.quantity * item.unit_price;
        
        return {
          invoice_id: invoice.id, // Referência à fatura criada
          subscription_plan_id: item.subscription_plan_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          billing_period: null,
          billing_period_unit: null,
          currency_code: subscription.currency_code || 'BRL',
          amount_price: itemAmount,
          created_by: user?.id || null,
          updated_by: user?.id || null
        };
      });
      
      console.log('Itens da fatura a serem inseridos:', invoiceItemsToInsert);
      
      const { error: invoiceItemsError } = await supabase
        .from('invoice_items')
        .insert(invoiceItemsToInsert);
      
      if (invoiceItemsError) {
        console.error('Erro ao criar itens da fatura:', invoiceItemsError);
        throw invoiceItemsError;
      }
      
      console.log('Itens da fatura criados com sucesso');
      
      // Registrar a operação na tabela de orders
      console.log('Registrando operação...');
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: id,
          operation_type: 'faturamento',
          quantity_change: null,
          operation_date: new Date().toISOString(),
          created_by: user?.id || null,
          updated_by: user?.id || null
        });
      
      if (orderError) {
        console.error('Erro ao registrar operação:', orderError);
        // Não lançar erro aqui, pois a fatura já foi criada
        toast({ title: "Atenção", description: 'Fatura criada, mas não foi possível registrar a operação' });
      } else {
        console.log('Operação registrada com sucesso');
      }
      
      toast({ title: "Sucesso", description: 'Fatura gerada com sucesso para todos os itens ativos da assinatura' });
      setIsInvoiceDialogOpen(false);
      
      // Atualizar a aba para mostrar as faturas
      setActiveTab('invoices');
      
      // Recarregar as faturas
      fetchInvoices(id);
    } catch (error) {
      console.error('Erro ao gerar fatura:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível gerar a fatura: ' + (error instanceof Error ? error.message : 'Erro desconhecido') });
    } finally {
      setIsSubmittingInvoice(false);
    }
  };

  // Função para navegar para detalhes da fatura
  const handleInvoiceClick = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}`);
  };

  // Adicionar função para abrir o modal de geração de fatura
  const openInvoiceDialog = async () => {
    if (!subscription) return;
    
    try {
      // Buscar todos os itens ativos da assinatura
      const { data: activeItems, error: itemsError } = await supabase
        .from('subscription_items')
        .select(`
          id,
          quantity,
          unit_price,
          subscription_plan:subscription_plan_id (
            name,
            prices:subscription_plan_prices (
              currency,
              price_amount
            )
          )
        `)
        .eq('subscription_id', id)
        .neq('state', 'canceled');
      
      if (itemsError) {
        throw itemsError;
      }
      
      if (!activeItems || activeItems.length === 0) {
        toast({ variant: "destructive", title: "Erro", description: 'Não há itens ativos nesta assinatura para faturar' });
        return;
      }
      
      // Calcular o valor total de todos os itens ativos
      const totalAmount = activeItems.reduce((sum, item) => {
        return sum + (item.quantity * item.unit_price);
      }, 0);
      
      // Gerar um número de fatura padrão
      setInvoiceNumber(generateInvoiceNumber());
      
      // Definir a data da fatura como hoje
      const invoiceDate = new Date();
      setInvoiceDate(invoiceDate);
      
      // Calcular a data de vencimento com base nas configurações da assinatura
      let calculatedDueDate = new Date(invoiceDate);
      
      if (subscription.due_date_type === 'fixed_day') {
        // Vencimento é em um dia fixo do mês
        const dueDateDay = subscription.due_date_day || 5; // Valor padrão: dia 5
        
        if (subscription.due_date_next_month) {
          // Vencimento no próximo mês
          calculatedDueDate.setMonth(invoiceDate.getMonth() + 1);
        }
        
        // Definir o dia do mês
        calculatedDueDate.setDate(dueDateDay);
        
        // Se o dia já passou no mês atual e não estamos usando próximo mês, mova para o próximo mês
        if (!subscription.due_date_next_month && calculatedDueDate <= invoiceDate) {
          calculatedDueDate.setMonth(calculatedDueDate.getMonth() + 1);
        }
      } else if (subscription.due_date_type === 'days_after_invoice') {
        // Vencimento é um número de dias após a emissão
        const daysAfter = subscription.due_date_day || 30; // Valor padrão: 30 dias
        calculatedDueDate.setDate(invoiceDate.getDate() + daysAfter);
      } else {
        // Comportamento padrão: 30 dias após a data atual
        calculatedDueDate.setDate(invoiceDate.getDate() + 30);
      }
      
      setDueDate(calculatedDueDate);
      
      // Armazenar os itens ativos para exibição no modal
      setActiveSubscriptionItems(activeItems);
      
      setIsInvoiceDialogOpen(true);
    } catch (error) {
      console.error('Erro ao preparar geração de fatura:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível preparar a geração de fatura' });
    }
  };

  // Função para abrir o modal de cancelamento de assinatura
  const openCancelDialog = () => {
    setCancelReasonCategory('');
    setCancelDetails('');
    setIsCancelDialogOpen(true);
  };

  // Função para processar o cancelamento da assinatura
  const handleCancelSubscription = async () => {
    if (!subscription || !id) {
      toast({ variant: "destructive", title: "Erro", description: 'Dados da assinatura não disponíveis' });
      return;
    }
    
    if (!cancelReasonCategory) {
      toast({ variant: "destructive", title: "Erro", description: 'Por favor, selecione um motivo para o cancelamento' });
      return;
    }
    
    setIsSubmittingCancellation(true);
    
    try {
      console.log('Iniciando cancelamento da assinatura:', id);
      
      // Verificar se a assinatura já está cancelada
      if (subscription.state.toLowerCase() === 'canceled') {
        toast({ variant: "destructive", title: "Erro", description: 'Esta assinatura já está cancelada' });
        setIsSubmittingCancellation(false);
        setIsCancelDialogOpen(false);
        return;
      }

      // Formatar o motivo do cancelamento
      const formattedReason = cancelDetails 
        ? `${cancelReasonCategory}: ${cancelDetails}` 
        : cancelReasonCategory;
      
      // Buscar todos os itens ativos da assinatura primeiro
      const { data: activeItems, error: fetchItemsError } = await supabase
        .from('subscription_items')
        .select('id, subscription_plan_id')
        .eq('subscription_id', id)
        .eq('state', 'active');
        
      if (fetchItemsError) {
        console.error('Erro ao buscar itens ativos da assinatura:', fetchItemsError);
        throw new Error('Não foi possível buscar os itens ativos da assinatura');
      }
      
      console.log(`Encontrados ${activeItems?.length || 0} itens ativos para cancelar`);
      
      // Atualizar o estado da assinatura para "canceled"
      const { error: subscriptionError } = await supabase
        .from('subscriptions')
        .update({
          state: 'canceled',
          canceled_at: new Date().toISOString(),
          cancellation_reason: formattedReason,
          updated_by: 'user'
        })
        .eq('id', id);
      
      if (subscriptionError) {
        console.error('Erro ao cancelar assinatura:', subscriptionError);
        throw new Error('Não foi possível cancelar a assinatura');
      }
      
      // Se houver itens ativos, cancelá-los
      if (activeItems && activeItems.length > 0) {
        // Cancelar todos os itens ativos da assinatura
        const { error: itemsError } = await supabase
          .from('subscription_items')
          .update({
            state: 'canceled',
            canceled_at: new Date().toISOString(),
            cancellation_reason: formattedReason,
            updated_by: 'user'
          })
          .eq('subscription_id', id)
          .eq('state', 'active');
        
        if (itemsError) {
          console.error('Erro ao cancelar itens da assinatura:', itemsError);
          toast({ title: "Atenção", description: 'Assinatura cancelada, mas alguns itens podem não ter sido atualizados' });
        } else {
          console.log('Todos os itens ativos da assinatura foram cancelados com sucesso');
          
          // Registrar operações de cancelamento para cada item
          const orderPromises = activeItems.map(item => 
            supabase
              .from('orders')
              .insert({
                subscription_id: id,
                subscription_item_id: item.id,
                subscription_plan_id: item.subscription_plan_id,
                operation_type: 'cancelamento',
                quantity_change: null,
                operation_date: new Date().toISOString(),
                created_by: 'user',
                notes: `Item cancelado: ${formattedReason}`
              })
          );
          
          try {
            await Promise.all(orderPromises);
            console.log('Operações de cancelamento de itens registradas com sucesso');
          } catch (orderError) {
            console.error('Erro ao registrar algumas operações de cancelamento de itens:', orderError);
            toast({ title: "Atenção", description: 'Alguns registros de operações podem não ter sido criados' });
          }
        }
      } else {
        console.log('Não há itens ativos para cancelar nesta assinatura');
      }
      
      // Registrar a operação principal de cancelamento da assinatura
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: id,
          operation_type: 'cancelamento',
          quantity_change: null,
          operation_date: new Date().toISOString(),
          created_by: 'user',
          notes: `Assinatura cancelada: ${formattedReason}`
        });
      
      if (orderError) {
        console.error('Erro ao registrar operação de cancelamento:', orderError);
        toast({ title: "Atenção", description: 'Assinatura cancelada, mas não foi possível registrar a operação' });
      } else {
        console.log('Operação de cancelamento registrada com sucesso');
      }
      
      toast({ title: "Sucesso", description: 'Assinatura cancelada com sucesso' });
      
      // Recarregar os dados da assinatura
      await fetchSubscriptionDetails(id);
      
      // Atualizar a aba de operações se estiver ativa
      if (activeTab === 'orders') {
        fetchOrders(id);
      }
      
      // Fechar o modal
      setIsCancelDialogOpen(false);
    } catch (error) {
      console.error('Erro ao cancelar assinatura:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível cancelar a assinatura: ' + (error instanceof Error ? error.message : 'Erro desconhecido') });
    } finally {
      setIsSubmittingCancellation(false);
    }
  };

  // Corrigir a função fetchInvoices para garantir que ela funcione corretamente
  const fetchInvoices = async (subscriptionId: string) => {
    setInvoicesLoading(true);
    try {
      console.log('Buscando faturas para a assinatura:', subscriptionId);
      
      // Buscar faturas diretamente da tabela invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select('*')
        .eq('subscription_id', subscriptionId)
        .order('invoice_date', { ascending: false });
      
      if (invoicesError) {
        console.error('Erro ao buscar faturas:', invoicesError);
        throw invoicesError;
      }
      
      console.log('Faturas encontradas:', invoicesData);
      
      if (!invoicesData || invoicesData.length === 0) {
        console.log('Nenhuma fatura encontrada para esta assinatura');
        setInvoices([]);
        return;
      }
      
      // Para cada fatura, buscar seus itens
      const formattedInvoices = [];
      
      for (const invoice of invoicesData) {
        // Buscar itens da fatura
        const { data: invoiceItems, error: itemsError } = await supabase
        .from('invoice_items')
        .select(`
          id,
            quantity,
            unit_price,
            amount_price,
            subscription_plan:subscription_plan_id (
              name
            )
          `)
          .eq('invoice_id', invoice.id);
        
        if (itemsError) {
          console.error(`Erro ao buscar itens da fatura ${invoice.id}:`, itemsError);
          continue; // Pular esta fatura e continuar com as outras
        }
        
        // Formatar os itens
        const formattedItems = invoiceItems?.map(item => {
          // Safely access nested properties
          let planName = 'Plano sem nome';
          if (item.subscription_plan && 
              typeof item.subscription_plan === 'object' && 
              'name' in item.subscription_plan) {
            // Use type assertion to handle the unknown type
            const name = (item.subscription_plan as any).name;
            planName = typeof name === 'string' ? name : 'Plano sem nome';
          }
          
          return {
            id: item.id,
            amount_due: item.amount_price || (item.quantity * item.unit_price),
            plan_name: planName
          };
        }) || [];
        
        // Adicionar a fatura formatada
        formattedInvoices.push({
          id: invoice.id,
            invoice_number: invoice.invoice_number,
            invoice_date: invoice.invoice_date,
            due_date: invoice.due_date,
            paid_date: invoice.paid_date,
            status: invoice.status,
            currency: invoice.currency,
          total_amount: invoice.amount_due,
          items: formattedItems
        });
      }
      
      console.log('Faturas formatadas:', formattedInvoices);
      setInvoices(formattedInvoices);
    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível carregar as faturas' });
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Adicionar console.log para depuração no useEffect
  useEffect(() => {
    console.log('activeTab mudou para:', activeTab);
    if (id && activeTab === 'invoices') {
      console.log('Aba de faturas selecionada, carregando faturas...');
      fetchInvoices(id);
    }
  }, [id, activeTab]);

  // Adicionar função para exibir o status da fatura
  const getInvoiceStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    
    if (statusLower === 'paid') {
      return <Badge variant="default">Pago</Badge>;
    } else if (statusLower === 'pending') {
      return <Badge variant="secondary">Pendente</Badge>;
    } else if (statusLower === 'canceled') {
      return <Badge variant="destructive">Cancelado</Badge>;
    } else if (statusLower === 'overdue') {
      return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">Atrasado</Badge>;
    }
    
    return <Badge variant="outline">{status}</Badge>;
  };

  // Function to open the subscription renewal dialog
  const openRenewDialog = async () => {
    if (!subscription) return;
    
    try {
      // Fetch all active subscription items that are not one-time and have auto_renew = true
      const { data: activeItems, error: itemsError } = await supabase
        .from('subscription_items')
        .select(`
          id,
          quantity,
          unit_price,
          auto_renew,
          subscription_plan:subscription_plan_id (
            id,
            name,
            billing_period_type,
            billing_period_duration
          )
        `)
        .eq('subscription_id', id)
        .eq('state', 'active')
        .eq('auto_renew', true);
      
      if (itemsError) {
        throw itemsError;
      }
      
      // Filter out one-time items
      const filteredItems = activeItems?.filter(item => {
        const billingPeriodType = item.subscription_plan?.billing_period_type?.toLowerCase();
        return billingPeriodType !== 'one-time';
      });
      
      if (!filteredItems || filteredItems.length === 0) {
        toast({ variant: "destructive", title: "Erro", description: 'Não há itens renováveis com renovação automática ativa nesta assinatura' });
        return;
      }
      
      // Set renewal date - use subscription end_date if available, otherwise use current date
      const baseDate = subscription.end_date ? new Date(subscription.end_date) : new Date();
      setRenewDate(baseDate);
      
      // All fetched items are automatically selected for renewal
      const initialSelection: Record<string, boolean> = {};
      filteredItems.forEach(item => {
        initialSelection[item.id] = true;
      });
      
      setRenewableItems(filteredItems);
      setRenewItemsSelected(initialSelection);
      calculateNewEndDate(baseDate, filteredItems, initialSelection);
      
      setIsRenewDialogOpen(true);
    } catch (error) {
      console.error('Erro ao preparar renovação de assinatura:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível preparar a renovação da assinatura' });
    }
  };

  // Calculate the new end date based on all items with auto_renew = true
  const calculateNewEndDate = (baseDate: Date, items: any[], selectedItems: Record<string, boolean>) => {
    // Start with the base date
    let maxEndDate = new Date(baseDate);
    
    // Check each item and calculate its end date
    items.forEach(item => {
      const billingPeriodType = item.subscription_plan?.billing_period_type?.toLowerCase();
      const billingPeriodDuration = item.subscription_plan?.billing_period_duration || 1;
      
      let newEndDate = new Date(baseDate);
      
      // Calculate based on billing period
      if (billingPeriodType === 'monthly') {
        newEndDate.setMonth(newEndDate.getMonth() + billingPeriodDuration);
      } else if (billingPeriodType === 'yearly' || billingPeriodType === 'annually') {
        newEndDate.setFullYear(newEndDate.getFullYear() + billingPeriodDuration);
      } else if (billingPeriodType === 'quarterly') {
        newEndDate.setMonth(newEndDate.getMonth() + (3 * billingPeriodDuration));
      } else if (billingPeriodType === 'semiannually') {
        newEndDate.setMonth(newEndDate.getMonth() + (6 * billingPeriodDuration));
      } else {
        // Default to monthly if unknown
        newEndDate.setMonth(newEndDate.getMonth() + 1);
      }
      
      // Adjust for day of month to avoid unexpected date shifts
      // For example, Jan 31 + 1 month could become Mar 3 in some cases due to February
      const targetMonth = newEndDate.getMonth();
      const targetYear = newEndDate.getFullYear();
      // Get the last day of the target month if the day exceeds the number of days in the month
      const lastDayOfMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
      const targetDay = Math.min(baseDate.getDate(), lastDayOfMonth);
      newEndDate.setDate(targetDay);
      
      // Keep the latest end date
      if (newEndDate > maxEndDate) {
        maxEndDate = newEndDate;
      }
    });
    
    setCalculatedEndDate(maxEndDate);
  };

  // Function to handle renewal of subscription
  const handleRenewSubscription = async () => {
    if (!subscription || !id || !renewDate || !calculatedEndDate) {
      toast({ variant: "destructive", title: "Erro", description: 'Dados insuficientes para renovar a assinatura' });
      return;
    }
    
    if (renewableItems.length === 0) {
      toast({ variant: "destructive", title: "Erro", description: 'Não há itens para renovar' });
      return;
    }
    
    setIsSubmittingRenewal(true);
    
    try {
      // Update the subscription with new end date
      const { error: subscriptionError } = await supabase
        .from('subscriptions')
        .update({
          end_date: calculatedEndDate.toISOString(),
          renewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: 'user'
        })
        .eq('id', id);
      
      if (subscriptionError) {
        throw subscriptionError;
      }
      
      // Update terms for each auto-renewable item
      for (const item of renewableItems) {
        // Calculate the new term end date for this specific item
        const billingPeriodType = item.subscription_plan?.billing_period_type?.toLowerCase();
        const billingPeriodDuration = item.subscription_plan?.billing_period_duration || 1;
        
        let itemEndDate = new Date(renewDate);
        
        // Calculate based on billing period
        if (billingPeriodType === 'monthly') {
          itemEndDate.setMonth(itemEndDate.getMonth() + billingPeriodDuration);
        } else if (billingPeriodType === 'yearly' || billingPeriodType === 'annually') {
          itemEndDate.setFullYear(itemEndDate.getFullYear() + billingPeriodDuration);
        } else if (billingPeriodType === 'quarterly') {
          itemEndDate.setMonth(itemEndDate.getMonth() + (3 * billingPeriodDuration));
        } else if (billingPeriodType === 'semiannually') {
          itemEndDate.setMonth(itemEndDate.getMonth() + (6 * billingPeriodDuration));
        } else {
          // Default to monthly if unknown
          itemEndDate.setMonth(itemEndDate.getMonth() + 1);
        }
        
        // Adjust for day of month to avoid unexpected date shifts
        const targetMonth = itemEndDate.getMonth();
        const targetYear = itemEndDate.getFullYear();
        const lastDayOfMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
        const targetDay = Math.min(renewDate.getDate(), lastDayOfMonth);
        itemEndDate.setDate(targetDay);
        
        // Update the item with new term dates
        const { error: itemError } = await supabase
          .from('subscription_items')
          .update({
            current_term_start: renewDate.toISOString(),
            current_term_end: itemEndDate.toISOString(),
            updated_at: new Date().toISOString(),
            updated_by: 'user'
          })
          .eq('id', item.id);
        
        if (itemError) {
          console.error(`Erro ao atualizar item ${item.id}:`, itemError);
          toast({ title: "Atenção", description: `Não foi possível atualizar o item: ${item.subscription_plan?.name}` });
        }
        
        // Register the renewal operation for each item
        const { error: itemOrderError } = await supabase
          .from('orders')
          .insert({
            subscription_id: id,
            subscription_item_id: item.id,
            subscription_plan_id: item.subscription_plan?.id,
            operation_type: 'renovação',
            quantity_change: null,
            operation_date: new Date().toISOString(),
            created_by: 'user',
            notes: `Item renovado de ${formatDate(renewDate.toISOString())} até ${formatDate(itemEndDate.toISOString())}`
          });
        
        if (itemOrderError) {
          console.error(`Erro ao registrar operação para item ${item.id}:`, itemOrderError);
        }
      }
      
      // Register the main renewal operation
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: id,
          operation_type: 'renovação',
          quantity_change: null,
          operation_date: new Date().toISOString(),
          created_by: 'user',
          notes: `Assinatura renovada até ${formatDate(calculatedEndDate.toISOString())}`
        });
      
      if (orderError) {
        console.error('Erro ao registrar operação de renovação:', orderError);
        toast({ title: "Atenção", description: 'Assinatura renovada, mas não foi possível registrar a operação' });
      }
      
      toast({ title: "Sucesso", description: 'Assinatura renovada com sucesso' });
      
      // Reload the subscription data
      await fetchSubscriptionDetails(id);
      
      // Update the operations tab if active
      if (activeTab === 'orders') {
        fetchOrders(id);
      }
      
      // Close the dialog
      setIsRenewDialogOpen(false);
    } catch (error) {
      console.error('Erro ao renovar assinatura:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível renovar a assinatura: ' + (error instanceof Error ? error.message : 'Erro desconhecido') });
    } finally {
      setIsSubmittingRenewal(false);
    }
  };

  // Função para obter gradiente baseado no status
  const getStatusGradient = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'from-green-500 via-green-600 to-green-700';
      case 'canceled':
        return 'from-red-500 via-red-600 to-red-700';
      case 'paused':
        return 'from-yellow-500 via-yellow-600 to-yellow-700';
      case 'expired':
        return 'from-orange-500 via-orange-600 to-orange-700';
      default:
        return 'from-[#3b65f5] via-[#ff4a8f] to-[#c8e600]';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-gradient-to-br from-[#3b65f5] via-[#ff4a8f] to-[#c8e600] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <Loader2 className="h-16 w-16 animate-spin mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Carregando Assinatura</h1>
              <p className="text-white/80">Aguarde enquanto buscamos os detalhes...</p>
            </div>
                </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-gradient-to-br from-red-500 via-red-600 to-red-700 overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <AlertCircle className="h-16 w-16 mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Assinatura Não Encontrada</h1>
              <p className="text-white/80 mb-6">A assinatura não foi encontrada em nossos registros.</p>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/subscriptions')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para Assinaturas
            </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Hero Section with Fixed Background Color */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate('/subscriptions')}
                  className="text-white hover:bg-white/20 border-white/30"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="h-8 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <CreditCard className="h-6 w-6" />
                  <span className="text-white/80">Assinatura</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                #{subscription?.display_id || `${subscription?.id?.substring(0, 6)}-${subscription?.id?.substring(6, 9)}` || ''}
                    </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-white/90">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>{subscription?.customer_name || 'Cliente não identificado'}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-bold">{formatCurrency(calculateTotalAmount(), subscription.currency_code || 'BRL')}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <StatusBadge 
                  status={subscription.state} 
                  context="subscription" 
                  className="bg-white/20 text-white border-white/30"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  >
                    <MoreHorizontal className="h-4 w-4 mr-2" />
                    Ações
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Gerenciar Assinatura</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={openRenewDialog} disabled={subscription.state === 'canceled'}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Renovar Assinatura
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openInvoiceDialog} disabled={subscription.state !== 'active'}>
                      <FileText className="h-4 w-4 mr-2" />
                      Gerar Fatura
                    </DropdownMenuItem>
                  <DropdownMenuItem onClick={openAddContactModal}>
                    <User className="h-4 w-4 mr-2" />
                    Adicionar Contato
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={openCancelDialog} className="text-red-600" disabled={subscription.state === 'canceled'}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Cancelar Assinatura
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                onClick={() => navigate('/subscriptions')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Todas
              </Button>
            </div>
            </div>
          </div>
        </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8">
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Status Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <Activity className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Status</span>
                    </div>
                      <StatusBadge status={subscription.state} context="subscription" />
                    </div>
                    <div className="text-xs text-gray-500">
                      Tipo: {getSubscriptionTypeLabel(subscription.subscription_type)}
                  </div>
                </CardContent>
              </Card>

          {/* Amount Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    </div>
                      <span className="text-sm font-extrabold text-gray-600">Valor Total</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {formatCurrency(calculateTotalAmount(), subscription.currency_code || 'BRL')}
                  </div>
                  </div>
                    <div className="text-xs text-gray-500">
                      {subscription.items?.length || 0} itens
                  </div>
                </CardContent>
              </Card>

          {/* Next Billing Card */}
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-orange-100 rounded-md">
                        <Calendar className="h-4 w-4 text-orange-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Próx. Cobrança</span>
                    </div>
                <div className="text-right">
                    <div className="text-lg font-extrabold text-gray-900">
                        {subscription.next_billing_at ? formatDate(subscription.next_billing_at) : 'N/A'}
                    </div>
                    </div>
              </div>
                    <div className="text-xs text-gray-500">
                Data de faturamento
                  </div>
                </CardContent>
              </Card>

          {/* Period Card */}
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-purple-100 rounded-md">
                    <Calendar className="h-4 w-4 text-purple-600" />
                    </div>
                  <span className="text-sm font-extrabold text-gray-600">Período</span>
                    </div>
                <div className="text-right">
                  <div className="text-sm font-extrabold text-gray-900">
                    {subscription.start_date ? 
                      `${formatDate(subscription.start_date)} - ${subscription.end_date ? formatDate(subscription.end_date) : '...'}` : 
                      'N/A'
                    }
                  </div>
                  </div>
                    </div>
              <div className="text-xs text-gray-500">
                Vigência
                  </div>
                </CardContent>
              </Card>
            </div>

        {/* Enhanced Main Content */}
        <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
          <CardContent className="p-6">
        {/* Tabs Navigation */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="flex bg-gray-100 rounded-lg p-1 mb-6 max-w-fit">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                      activeTab === 'overview'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <LayoutGrid className="h-4 w-4" />
                    Visão Geral
                  </button>

                  <button
                    onClick={() => setActiveTab('contacts')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                      activeTab === 'contacts'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Users className="h-4 w-4" />
                    Contatos
                  </button>
                  <button
                    onClick={() => setActiveTab('invoices')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                      activeTab === 'invoices'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Receipt className="h-4 w-4" />
                    Faturas
                  </button>
                  <button
                    onClick={() => setActiveTab('orders')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                      activeTab === 'orders'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <ClipboardList className="h-4 w-4" />
                    Operações
                  </button>
                </div>

          {/* Overview Tab Content */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Consolidated Subscription Information */}
              <Card className="lg:col-span-1 border shadow-sm bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    Informações da Assinatura
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                      <div>
                    <h3 className="text-sm font-extrabold text-gray-600 mb-2">Cliente</h3>
                    <div className="space-y-1">
                      <div className="font-extrabold text-gray-900">
                        {subscription?.customer_name || 'Cliente não identificado'}
                      </div>
                        {subscription?.legal_name && subscription?.legal_name !== subscription?.customer_name && (
                        <div className="text-sm text-gray-500">
                          {subscription?.legal_name}
                        </div>
                        )}
                      </div>
                    </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-sm font-extrabold text-gray-600 mb-2">Configuração</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Tipo</span>
                        <span className="text-sm font-extrabold">{getSubscriptionTypeLabel(subscription.subscription_type)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Itens</span>
                        <span className="text-sm font-extrabold">{subscription.items?.length || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Status</span>
                        <StatusBadge status={subscription.state} context="subscription" />
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                      <div>
                    <h3 className="text-sm font-extrabold text-gray-600 mb-2">Cronologia</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Início</span>
                        <span className="text-sm font-extrabold">{subscription.start_date ? formatDate(subscription.start_date) : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Término</span>
                        <span className="text-sm font-extrabold">{subscription.end_date ? formatDate(subscription.end_date) : 'Indefinido'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Próx. Cobrança</span>
                        <span className="text-sm font-extrabold">{subscription.next_billing_at ? formatDate(subscription.next_billing_at) : 'N/A'}</span>
                      </div>
                      {subscription.canceled_at && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">Cancelado em</span>
                          <span className="text-sm font-extrabold text-red-600">{formatDate(subscription.canceled_at)}</span>
                    </div>
                  )}
                    </div>
                  </div>
                  
                  <Separator />

                  {subscription.po_number && (
                    <>
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">PO Number</h3>
                        <div className="text-sm text-gray-900 font-mono">
                          {subscription.po_number}
                      </div>
                    </div>
                      <Separator />
                    </>
                  )}

                    <div>
                    <h3 className="text-sm font-extrabold text-gray-600 mb-2">Valor Total</h3>
                    <div className="text-lg font-extrabold text-green-600">
                      {formatCurrency(calculateTotalAmount(), subscription.currency_code || 'BRL')}
                    </div>
                  </div>
                  
                  {subscription.cancellation_reason && (
                    <>
                      <Separator />
                            <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Motivo do Cancelamento</h3>
                        <div className="text-sm text-gray-900 p-2 bg-red-50 rounded border border-red-200">
                          {subscription.cancellation_reason}
                              </div>
                            </div>
                    </>
                  )}
                  
                  {getParticipantCustomers().length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Clientes Participantes</h3>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                          <div>
                            <div className="font-extrabold text-gray-900 text-sm">{getParticipantCustomers().length} participante(s)</div>
                            <div className="text-xs text-gray-500">Clientes adicionais</div>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => setActiveTab('contacts')}>
                            <Users className="h-3 w-3 mr-1" />
                            Ver
                          </Button>
                          </div>
                        </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Subscription Items */}
              <Card className="lg:col-span-2 border shadow-sm bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="h-5 w-5 text-green-600" />
                    Itens da Assinatura
                </CardTitle>
                  <Badge variant="outline" className="text-xs h-7 px-3 border-green-200 text-green-700 bg-green-50">
                  {subscription.items?.length || 0} {subscription.items?.length === 1 ? 'item' : 'itens'}
                </Badge>
              </CardHeader>
              <CardContent>
                {subscription.subscription_type === 'main' && subscription.child_subscriptions && subscription.child_subscriptions.length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-700">
                      Visualização consolidada de {subscription.child_subscriptions.length} assinatura(s) filha(s)
                    </p>
                  </div>
                )}
                
                {subscription.items && subscription.items.length > 0 ? (
                    <div className="space-y-3">
                      {subscription.items.slice(0, 4).map((item) => (
                      <div 
                        key={item.id} 
                          className="group p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all duration-200 cursor-pointer bg-white"
                        onClick={() => {
                          if (!item.is_consolidated) {
                            navigate(`/subscription-items/${item.id}`);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-md bg-green-100 flex items-center justify-center flex-shrink-0">
                                <Package className="h-4 w-4 text-green-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-extrabold text-gray-900 text-sm leading-tight truncate">
                                  {item.subscription_plan?.name || 'Plano sem nome'}
                                </h3>
                                  {item.is_consolidated && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                      Consolidado
                                    </span>
                                  )}
                                  <StatusBadge status={item.state} context="subscription" />
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-600">
                                  <span>Qtd: {item.quantity || 1}</span>
                                  <span>•</span>
                                  <span>
                                    {item.subscription_plan?.billing_period_type ? 
                                      (item.subscription_plan.billing_period_duration ? 
                                        formatBillingPeriod(item.subscription_plan.billing_period_type, item.subscription_plan.billing_period_duration) : 
                                        getBillingPeriodTypeLabel(item.subscription_plan.billing_period_type)
                                      ) : 
                                      'Não definido'}
                                  </span>
                                  {(item.current_term_start && item.current_term_end) && (
                                    <>
                                      <span>•</span>
                                      <span className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {formatDate(item.current_term_start)} → {formatDate(item.current_term_end)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-3">
                              <div className="text-lg font-extrabold text-gray-900">
                              {item.is_consolidated && item.total_amount ? 
                                formatCurrency(item.total_amount, subscription.currency_code || 'BRL') :
                                formatCurrency((item.quantity || 1) * (item.unit_price || 0), subscription.currency_code || 'BRL')
                              }
                            </div>
                              <div className="text-xs text-gray-500">
                                {formatCurrency(item.unit_price || 0, subscription.currency_code || 'BRL')} /unidade
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                      {subscription.items.length > 4 && (
                        <div className="text-center py-2">
                          <div className="text-sm text-gray-500">
                            +{subscription.items.length - 4} item(s) adicional(is)
                      </div>
                    </div>
                      )}
                  </div>
                ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Package className="h-8 w-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500">Nenhum item encontrado</p>
                  </div>
                )}
              </CardContent>
            </Card>
            </div>

          </TabsContent>



          {/* Contacts Tab Content */}
          <TabsContent value="contacts" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Contatos da Assinatura</CardTitle>
                <Button variant="outline" size="sm" onClick={openAddContactModal}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </CardHeader>
              <CardContent>
                {subscription.contacts && subscription.contacts.length > 0 ? (
                  <div className="space-y-4">
                    {subscription.contacts.map((contactItem) => (
                      <div key={contactItem.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-extrabold text-slate-900 dark:text-slate-100">
                              {contactItem.contact?.first_name} {contactItem.contact?.last_name || ''}
                            </h4>
                            <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                              <span>{contactItem.contact?.email || 'Email não informado'}</span>
                              {contactItem.contact?.phone && (
                                <>
                                  <span>•</span>
                                  <span>{contactItem.contact.phone}</span>
                                </>
                              )}
                            </div>
                            <Badge variant="secondary" className="mt-1">
                              {getContactTypeLabel(contactItem.contact_type)}
                            </Badge>
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeContact(contactItem.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="space-y-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mx-auto">
                        <User className="h-12 w-12 text-purple-500" />
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-xl font-extrabold text-gray-900">
                          Nenhum contato associado
                        </h3>
                        <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                          Adicione contatos para facilitar a comunicação e gestão desta assinatura.
                        </p>
                      </div>
                      <Button variant="outline" size="lg" onClick={openAddContactModal}>
                        <Plus className="h-5 w-5 mr-2" />
                      Adicionar Primeiro Contato
                    </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab Content */}
          <TabsContent value="invoices" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Faturas</CardTitle>
                {subscription.state === 'active' && (
                  <Button variant="outline" size="sm" onClick={openInvoiceDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Gerar Fatura
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {invoicesLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : invoices && invoices.length > 0 ? (
                  <div className="space-y-4">
                    {invoices.map((invoice) => (
                      <div 
                        key={invoice.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => handleInvoiceClick(invoice.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <Receipt className="h-5 w-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <h4 className="font-extrabold text-slate-900 dark:text-slate-100">#{invoice.invoice_number}</h4>
                            <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                              <span>Emitida: {formatDate(invoice.invoice_date)}</span>
                              <span>•</span>
                              <span>Vence: {formatDate(invoice.due_date)}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <StatusBadge status={invoice.status} context="invoice" />
                              <span className="text-lg font-extrabold text-slate-900 dark:text-slate-100">
                                {formatCurrency(invoice.total_amount, invoice.currency || 'BRL')}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Aqui pode adicionar a lógica de download da fatura no futuro
                            console.log('Download fatura:', invoice.id);
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="space-y-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto">
                        <Receipt className="h-12 w-12 text-green-500" />
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-xl font-extrabold text-gray-900">
                          Nenhuma fatura encontrada
                        </h3>
                        <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                          Gere faturas para cobrança dos itens desta assinatura de forma automatizada.
                        </p>
                      </div>
                    {subscription.state === 'active' && (
                        <Button variant="outline" size="lg" onClick={openInvoiceDialog}>
                          <Plus className="h-5 w-5 mr-2" />
                        Gerar Primeira Fatura
                      </Button>
                    )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab Content */}
          <TabsContent value="orders" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Operações</CardTitle>
              </CardHeader>
              <CardContent>
                {ordersLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : subscription.orders && subscription.orders.length > 0 ? (
                  <div className="space-y-4">
                    {subscription.orders.map((order) => (
                      <div key={order.id} className="flex items-start gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                        <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                          <ClipboardList className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-extrabold text-slate-900 dark:text-slate-100">
                              {getOperationTypeLabel(order.operation_type)}
                            </h4>
                            {order.quantity_change !== null && (
                              <Badge variant="outline" className={
                                order.quantity_change > 0 ? 'text-green-700 border-green-300' : 
                                order.quantity_change < 0 ? 'text-red-700 border-red-300' : 
                                'text-slate-700 border-slate-300'
                              }>
                                {order.quantity_change > 0 ? '+' : ''}{order.quantity_change}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-600 dark:text-slate-400">
                            {formatDateTime(order.operation_date)}
                            {order.subscription_item?.subscription_plan.name && (
                              <>
                                <span className="mx-2">•</span>
                                <span>{order.subscription_item.subscription_plan.name}</span>
                              </>
                            )}
                          </div>
                          {order.notes && (
                            <p className="text-sm text-slate-700 dark:text-slate-300 mt-2 bg-slate-100 dark:bg-slate-700 p-2 rounded-lg">
                              {order.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="space-y-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full flex items-center justify-center mx-auto">
                        <ClipboardList className="h-12 w-12 text-orange-500" />
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-xl font-extrabold text-gray-900">
                          Nenhuma operação encontrada
                        </h3>
                        <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                          O histórico de operações desta assinatura aparecerá aqui conforme ações são executadas.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Opportunities Tab Content */}
          <TabsContent value="opportunities" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Oportunidades Relacionadas</CardTitle>
                <Button variant="outline" size="sm" onClick={() => navigate(`/business-opportunities/new?customer_name=${subscription?.customer_name}`)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Oportunidade
                </Button>
              </CardHeader>
              <CardContent>
                {opportunitiesLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : opportunities && opportunities.length > 0 ? (
                  <div className="space-y-4">
                    {opportunities.map((opportunity) => (
                      <div 
                        key={opportunity.id}
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => navigate(`/business-opportunities/${opportunity.id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <h4 className="font-extrabold text-slate-900 dark:text-slate-100">
                              {opportunity.name || opportunity.dealname}
                            </h4>
                            <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                              <span>
                                {formatCurrency(opportunity.total_value || opportunity.amount || 0, opportunity.currency_code || opportunity.deal_currency_code || 'BRL')}
                              </span>
                              <span>•</span>
                              <span>
                                {(opportunity.probability || opportunity.hs_deal_stage_probability || 0) <= 1 
                                  ? ((opportunity.probability || opportunity.hs_deal_stage_probability || 0) * 100).toFixed(1) + '%' 
                                  : (opportunity.probability || opportunity.hs_deal_stage_probability || 0) + '%'} prob.
                              </span>
                              {(opportunity.expected_close_date || opportunity.closedate) && (
                                <>
                                  <span>•</span>
                                  <span>Fecha: {formatDate(opportunity.expected_close_date || opportunity.closedate)}</span>
                                </>
                              )}
                            </div>
                            <div className="mt-1">
                              {getOpportunityStatusBadge(opportunity.status)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="space-y-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                        <Calendar className="h-12 w-12 text-indigo-500" />
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-xl font-extrabold text-gray-900">
                          Nenhuma oportunidade relacionada
                        </h3>
                        <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                          Crie oportunidades de negócio relacionadas a esta assinatura para acompanhar vendas futuras.
                        </p>
                      </div>
                    <Button 
                      variant="outline" 
                        size="lg"
                      onClick={() => navigate(`/business-opportunities/new?customer_name=${subscription?.customer_name}`)}
                    >
                        <Plus className="h-5 w-5 mr-2" />
                      Criar Nova Oportunidade
                    </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Spacing */}
      <div className="h-16" />

      {/* Invoice Generation Modal */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Gerar Nova Fatura
            </DialogTitle>
            <DialogDescription>
              A fatura incluirá todos os itens ativos da assinatura.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data da Fatura</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {invoiceDate ? format(invoiceDate, "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent 
                      mode="single" 
                      selected={invoiceDate} 
                      onSelect={(date) => date && setInvoiceDate(date)} 
                      initialFocus 
                      locale={ptBR} 
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Data de Vencimento</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent 
                      mode="single" 
                      selected={dueDate} 
                      onSelect={(date) => date && setDueDate(date)} 
                      initialFocus 
                      locale={ptBR} 
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Itens Incluídos</Label>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plano</TableHead>
                      <TableHead className="text-center">Qtd.</TableHead>
                      <TableHead>Preço Unit.</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeSubscriptionItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-extrabold">
                          {item.subscription_plan?.name || 'N/A'}
                        </TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell>
                          {formatCurrency(item.unit_price, subscription?.currency_code || 'BRL')}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.quantity * item.unit_price, subscription?.currency_code || 'BRL')}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-extrabold">Total:</TableCell>
                      <TableCell className="text-right font-extrabold">
                        {formatCurrency(
                          activeSubscriptionItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0), 
                          subscription?.currency_code || 'BRL'
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInvoiceDialogOpen(false)} disabled={isSubmittingInvoice}>
              Cancelar
            </Button>
            <Button 
              onClick={handleGenerateInvoice} 
              disabled={isSubmittingInvoice || !invoiceDate || !dueDate || !invoiceNumber}
            >
              {isSubmittingInvoice ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando
                </>
              ) : (
                'Gerar Fatura'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionDetail; 